import clsx from 'clsx';
import { Scrollable } from '../Scrollable/index.ts';

const styles = {
  section: clsx('px-(--gutter-x) py-(--gutter-y)')
};

export interface CardProps {
  bleed?: false | 'mobile';
  children?: React.ReactNode;
  footer?: React.ReactNode;
  fillContainer?: boolean;
  header?: React.ReactNode;
  padding?: 'tight' | 'normal' | 'extra';
  scrollable?: boolean;
  shaded?: boolean;
  shadow?: 'md' | 'lg';
  subduedHeaderFooter?: boolean;
}

export function Card({
  bleed = false,
  children,
  footer,
  fillContainer = false,
  header,
  padding = 'normal',
  scrollable,
  shaded = false,
  shadow,
  subduedHeaderFooter
}: CardProps) {
  return (
    <div
      className={clsx(
        'overflow-hidden',
        scrollable && 'flex max-h-full flex-col',
        !subduedHeaderFooter && 'divide-y divide-gray-200',
        fillContainer && 'size-full',

        // Background
        shaded ? 'bg-gray-50' : 'bg-white',

        // Shadow
        shadow === 'md' && 'shadow-md',
        shadow === 'lg' && 'shadow-lg',

        // Border
        !shaded && ['border-gray-200 sm:border', bleed ? 'border-t border-b' : 'border'],

        // Border radius
        !bleed && 'rounded-xl',
        bleed && 'sm:rounded-xl',

        // Bleed
        bleed && '-mx-(--gutter-x) sm:mx-0',

        // Gutter values
        padding === 'tight' && [
          '[--gutter-y:--spacing(4)] sm:[--gutter-x:--spacing(5)]',
          !bleed && '[--gutter-x:--spacing(4)]'
        ],
        padding === 'normal' && [
          '[--gutter-y:--spacing(5)] sm:[--gutter-x:--spacing(6)] sm:[--gutter-y:--spacing(6)]',
          !bleed && '[--gutter-x:--spacing(4)]'
        ],
        padding === 'extra' && [
          '[--gutter-y:--spacing(8)] sm:[--gutter-x:--spacing(10)]',
          !bleed && '[--gutter-x:--spacing(6)]'
        ]
      )}
    >
      {header && (
        <header
          className={clsx(
            'z-1 flex-none',
            subduedHeaderFooter && 'bg-gray-50',
            padding === 'tight' && '[--gutter-y:--spacing(4)]',
            padding === 'normal' && '[--gutter-y:--spacing(5)]',
            padding === 'extra' && '[--gutter-y:--spacing(5)] sm:[--gutter-y:--spacing(6)]'
          )}
        >
          <div className={styles.section}>{header}</div>
        </header>
      )}

      {scrollable ? (
        <div className={clsx(scrollable && 'flex min-h-0 grow flex-col')}>
          <Scrollable className="flex min-h-0 flex-col">
            <div className={styles.section}>{children}</div>
          </Scrollable>
        </div>
      ) : (
        <div className={clsx(styles.section, fillContainer && 'size-full')}>{children}</div>
      )}

      {footer && (
        <div
          className={clsx(
            'z-1 flex-none',
            subduedHeaderFooter && 'bg-gray-50',
            padding === 'tight' && '[--gutter-y:--spacing(3.5)]',
            padding === 'normal' && '[--gutter-y:--spacing(4.5)]',
            padding === 'extra' && '[--gutter-y:--spacing(4.5)] sm:[--gutter-y:--spacing(5)]'
          )}
        >
          <div className={styles.section}>{footer}</div>
        </div>
      )}
    </div>
  );
}
